import React, { useEffect, useState } from 'react'

import cx from 'classnames'
import { Form } from 'react-bootstrap'

import { Fonts } from '@paperflite-marketing/cleverstory-commons'
import { getColorsConfig } from '../../../../AssetViewer/helper/cssHelper'
import { FormWidgetContext } from '../..'
import { injectAktivFallback } from '../../../../widgets/widget.helper'

import styles from './checkBoxList.module.scss'
import get from 'lodash/get';
import map from 'lodash/map';
import some from 'lodash/some';
import uniqBy from 'lodash/uniqBy';


const FomrCheckBoxListWidget = function TextInputEditor({
  className,
  style,
  widgetProps,
  breakpoint
}) {
  const context = React.useContext(FormWidgetContext)
  const [checkedItems, setCheckedItems] = useState([]);
  const [errors, setErrors] = useState('');

  const id = get(widgetProps, 'uid');
  const formId = get(widgetProps, 'parent')
  const properties = get(widgetProps, 'properties', {});
  const label = get(widgetProps, 'properties.desktop.label') || '';
  const multiColumn = get(widgetProps, 'properties.desktop.multiColumn') || false;
  const multiSelect = get(widgetProps, "properties.desktop.multiSelect") || false;
  const field = get(widgetProps, 'properties.desktop.field') || '';
  const fieldId = get(widgetProps, 'content.id') || "";
  const listOptions = get(widgetProps, 'content.options', []);
  const error = get(context.errors, id) || '';

  useEffect(() => {
    setErrors('');
    const values = checkedItems.map(item => item.id);
    context.updateField(id, field, fieldId, values)
  }, [checkedItems])

  const handleInputChange = (selectedOption) => {    
    setCheckedItems(prevState => {
      if(multiSelect) {
        const alreadyExist = prevState.some(item => item.id === selectedOption.id);
        if(alreadyExist) {
          return prevState.filter(item => item.id !== selectedOption.id);
        }else {
          return uniqBy([...prevState, selectedOption], 'id');
        }
      }else {
        return [selectedOption];
      }
    });
  };

  useEffect(()=>{
    setErrors(error);
  }, [error])

  Fonts.useFont(get(widgetProps, 'fonts') || [])

  var widgetStyles = {
    ...style,
    ...getFieldStyles(properties, breakpoint, 'regular')
  }

  var labelStyles = getLabelStyles(properties, breakpoint, 'regular');
  var containerStyles = getContainerStyles(properties, breakpoint, 'regular');
  var inputStyles = getInputStyles(properties, breakpoint, 'regular');

  return (
    <div
      id={id}
      className={cx(className, styles.checkBoxField)}
      style={widgetStyles}
      data-widget-id={id}
    >
      {label && <label style={labelStyles}>{label}</label>}
      <div style={containerStyles} className={cx(styles.container, { [styles.singleColumn]: !multiColumn, [styles.multiColumn]: multiColumn })}>
        {map(listOptions, (option, index) => {
          const isChecked = some(checkedItems, { id: option.id });
          return (
            <Form.Group
              key={`check_wrapper_${index}`}
              id={`${formId}_${option.id}`}
              className={styles.listItem}
              controlId={option.id}
              style={inputStyles}
            >
              <Form.Check
                id={`${formId}_${option.id}`}
                className={cx({[styles.checkbox]: multiSelect, [styles.radioBtn]: !multiSelect })}
                type={multiSelect ? 'checkbox' : 'radio'}
                label={option.value}
                checked={isChecked}
                onChange={() => handleInputChange(option)}
              />
            </Form.Group>
          )
        })}
      </div>
      <span className={cx(styles.error, { [styles.active]: errors })}>
        {errors}
      </span>
    </div>
  )
}

/**
 * --------------------------------------------------
 * Field Styles
 * --------------------------------------------------
 */
function getFieldStyles(properties, breakpoint, active) {
  var styles = {}

  if (!properties) {
    return styles
  }

  const defaultProperties = get(properties, `desktop`)
  const deviceProperties = get(properties, `${breakpoint}`)
  const activeStyles = get(defaultProperties, `${active}`, {})

  //alignment
  const alignment = get(deviceProperties, 'styles.layout.alignment') || 'left'
  styles['alignItems'] = alignment

  if (alignment === 'left') {
    styles['alignItems'] = 'flex-start'
  } else if (alignment === 'right') {
    styles['alignItems'] = 'flex-end'
  }

  return styles
}

/**
 * --------------------------------------------------
 * Label Styles
 * --------------------------------------------------
 */
function getLabelStyles(properties, breakpoint, active) {
  var styles = {}

  if (!properties) {
    return styles
  }

  const defaultProperties = get(properties, `desktop`)
  const deviceProperties = get(properties, `${breakpoint}`)
  const activeStyles = get(defaultProperties, `${active}`, {})

  const alignment = get(deviceProperties, 'styles.layout.alignment') || 'left'

  const fieldSpacing = get(deviceProperties, 'styles.layout.fieldSpacing') || 0
  styles['paddingBottom'] = `${fieldSpacing}px`

  const labelPadding = get(deviceProperties, 'styles.layout.labelPadding') || 0
  if (alignment === 'left') {
    styles['paddingLeft'] = `${labelPadding}px`
  } else if (alignment === 'right') {
    styles['paddingRight'] = `${labelPadding}px`
  }

  const inputStyles = get(defaultProperties, 'styles.regular.label')
  const fontStyles = getFontStyle(inputStyles)

  styles = {
    ...styles,
    ...fontStyles
  }

  return styles
}

/**
 * --------------------------------------------------
 * Container Styles
 * --------------------------------------------------
 */
function getContainerStyles(properties, breakpoint) {
  var styles = {}

  if (!properties) {
    return styles
  }

  const deviceProperties = get(properties, `${breakpoint}`)

  const spaceBetweenItems = get(deviceProperties, 'styles.layout.spaceBetweenItems') || 0;
  styles['rowGap'] = `${spaceBetweenItems}px`

  const inputPadding = get(deviceProperties, 'styles.layout.inputPadding') || 0;
  styles['paddingLeft'] = `${inputPadding}px`

  return styles
}

/**
 * --------------------------------------------------
 * Input Styles
 * --------------------------------------------------
 */
function getInputStyles(properties, breakpoint, active) {
  var styles = {}

  if (!properties) {
    return styles
  }

  const defaultProperties = get(properties, `desktop`)
  const deviceProperties = get(properties, `${breakpoint}`)

  const alignment = get(deviceProperties, 'styles.layout.alignment') || 'left'
  const inputPadding =
    get(deviceProperties, 'styles.layout.inputPadding') || 0

  if (alignment === 'left') {
    styles['paddingLeft'] = `${inputPadding}px`
  } else if (alignment === 'right') {
    styles['paddingRight'] = `${inputPadding}px`
  }

  const inputStyles = get(defaultProperties, 'styles.regular.input')
  const fontStyles = getFontStyle(inputStyles)

  styles = {
    ...styles,
    ...fontStyles
  }

  const border = get(defaultProperties, 'styles.regular.border')
  const borderStyles = getBorderStyles(border)

  styles = {
    ...styles,
    ...borderStyles
  }

  return styles
}

/**
 * --------------------------------------------------
 * Placeholder Styles
 * --------------------------------------------------
 */

export function getColorStyles(value) {
  if (!value) {
    return null
  }

  const colorRgb = value

  var color = 'inherit'
  if (colorRgb) {
    color = getColorsConfig(colorRgb)
  }

  var styles = {
    color: color
  }

  return styles
}

export function getBorderStyles(border) {
  var styles = {}
  if (!border) {
    return styles
  }

  const topLeft = get(border, 'radius.topLeft', null)
  const topRight = get(border, 'radius.topRight', null)
  const bottomLeft = get(border, 'radius.bottomLeft', null)
  const bottomRight = get(border, 'radius.bottomRight', null)

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px'
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px'
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px'
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px'
  }

  const borderStyle = get(border, 'style') || 'bottom'
  if (borderStyle !== 'none') {
    const borderWidthString = get(border, 'width') || 0
    var borderWidget = parseInt(borderWidthString) + 'px'

    const fieldName = borderStyle == 'all' ? 'borderWidth' : 'borderBottomWidth'
    styles[fieldName] = borderWidget

    const colorRgb = get(border, 'color')
    var color = 'transparent'
    if (colorRgb) {
      color = getColorsConfig(colorRgb)
    }

    styles['borderColor'] = color
  }

  return styles
}

export function getFontStyle(fontStyles) {
  var styles = {}

  if (!fontStyles) {
    return styles
  }

  //font-color
  const colorRgb = get(fontStyles, 'color', null)
  const color = getColorStyles(colorRgb)
  styles = {
    ...styles,
    ...color
  }

  //font-family
  const fontFamily = get(fontStyles, 'font.fontFamily', 'inherit')
  styles = {
    ...styles,
    fontFamily: injectAktivFallback(fontFamily)
  }

  //font-size
  const fontSizeString = get(fontStyles, `font.fontSize`) || 14
  if (fontSizeString) {
    var fontSize = parseInt(fontSizeString) + 'px'
    styles['fontSize'] = fontSize
  }

  return styles
}

export { FomrCheckBoxListWidget }
